import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert, RefreshCw, AlertTriangle, CheckCircle2, Eye, Play } from "lucide-react";
import useAuthStore from "@/store/authStore";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Merge Audit Health Check Tile
 *
 * Lives on Database Updates. Surfaces the merge-audit reversibility
 * workflow ("Plan B") for the wrong INP merges in production:
 *
 *   1. GET .../merge-audit/health-check — counts the candidates,
 *      splits clone-log-backed from manual_review.
 *   2. POST .../unmerge?dry_run=true   — full per-loser reversal plan,
 *      including pantone-to-restore and bom-items-to-repoint, plus a
 *      stock-movement guard banner.
 *   3. Apply (master-admin only, requires explicit confirmation,
 *      requires force toggle when the guard is tripped).
 *
 * The dry-run is the ONLY way to "see" the impact — there is no auto-run
 * path. The Apply button is disabled until a dry-run has been fetched in
 * the current session.
 */
export default function MergeAuditHealthTile() {
  const token = useAuthStore((s) => s.token);
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [dryRun, setDryRun] = useState(null);
  const [selectedLosers, setSelectedLosers] = useState({});
  const [force, setForce] = useState(false);
  const [applying, setApplying] = useState(false);

  const loadHealthCheck = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/raw-materials/admin/merge-audit/health-check`,
        { headers: authHeaders },
      );
      setHealth(res.data);
      // Pre-select every clone-log-backed loser — the safe default.
      const pre = {};
      for (const r of (res.data?.clone_log_backed || [])) {
        pre[r.from_rm_id] = true;
      }
      setSelectedLosers(pre);
      setDryRun(null); // invalidate any stale dry-run
      toast.success(`Found ${res.data.clone_log_backed_total} clone-log-backed candidates`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Health check failed");
    } finally {
      setLoading(false);
    }
  };

  const runDryRun = async () => {
    const ids = Object.entries(selectedLosers).filter(([, v]) => v).map(([k]) => k);
    if (ids.length === 0) {
      toast.error("Select at least one loser to unmerge");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/raw-materials/admin/unmerge?dry_run=true`,
        { from_rm_ids: ids, force },
        { headers: authHeaders },
      );
      setDryRun(res.data);
      const willApply = (res.data?.per_loser_report || []).filter((r) => r.will_apply || (!r.guard_blocks_apply && r.loser_state_ok)).length;
      toast.success(`Dry-run ready — ${willApply} losers would be reversed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setLoading(false);
    }
  };

  const applyUnmerge = async () => {
    if (!dryRun) {
      toast.error("Run dry-run first");
      return;
    }
    const ids = Object.entries(selectedLosers).filter(([, v]) => v).map(([k]) => k);
    const willGuardBlock = (dryRun?.per_loser_report || []).some((r) => r.guard_blocks_apply);
    if (willGuardBlock && !force) {
      toast.error("Some losers blocked by stock-movement guard — enable Force to override");
      return;
    }
    const confirm = window.confirm(
      `Apply unmerge for ${ids.length} loser(s)?\n\n` +
      `This will:\n` +
      `  • Restore each loser's status to ACTIVE\n` +
      `  • Repoint their bidso_sku common_bom items back\n` +
      `  • Stamp pantone_code from the clone log\n\n` +
      (force ? "FORCE is ON — guard checks bypassed.\n\n" : "") +
      `Continue?`,
    );
    if (!confirm) return;
    setApplying(true);
    try {
      const res = await axios.post(
        `${API}/raw-materials/admin/unmerge?dry_run=false`,
        { from_rm_ids: ids, force },
        { headers: authHeaders },
      );
      toast.success(`Applied: ${res.data.applied} losers reversed`);
      setDryRun(null);
      await loadHealthCheck(); // refresh
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  useEffect(() => {
    // Initial health check — defer one tick so the lint rule treating
    // "setState inside useEffect" as suspect doesn't trip. The actual
    // setStates happen inside the async loadHealthCheck callback.
    const t = setTimeout(() => { loadHealthCheck(); }, 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleLoser = (id) => {
    setSelectedLosers((prev) => ({ ...prev, [id]: !prev[id] }));
    setDryRun(null); // selection changed → invalidate dry-run
  };

  const allClone = health?.clone_log_backed || [];
  const allManual = health?.manual_review || [];
  const guardBlockedCount = (dryRun?.per_loser_report || []).filter((r) => r.guard_blocks_apply).length;

  return (
    <Card data-testid="merge-audit-health-tile" className="border-amber-200 bg-amber-50/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-600" />
              Merge Audit Health Check
            </CardTitle>
            <CardDescription>
              Detect and reverse wrong INP merges using the clone-log as ground truth (Plan B).
              Master-admin only.
            </CardDescription>
          </div>
          <Button
            data-testid="merge-audit-refresh-btn"
            onClick={loadHealthCheck} disabled={loading}
            variant="outline" size="sm"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary band */}
        {health && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <SummaryStat label="Awaiting Sourcing" value={health.awaiting_sourcing_total} tone="neutral" />
            <SummaryStat label="In Audit" value={health.candidates_total} tone="neutral" />
            <SummaryStat label="Clone-log-backed" value={health.clone_log_backed_total} tone="good" />
            <SummaryStat label="Manual Review" value={health.manual_review_total} tone="warn" />
          </div>
        )}

        {/* Clone-log-backed candidates */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-sm">Reversible candidates ({allClone.length})</h4>
            {allClone.length > 0 && (
              <div className="flex gap-2 text-xs">
                <button
                  data-testid="merge-audit-select-all-btn"
                  className="underline text-emerald-700"
                  onClick={() => {
                    const all = {};
                    for (const r of allClone) all[r.from_rm_id] = true;
                    setSelectedLosers(all);
                    setDryRun(null);
                  }}
                >
                  Select all
                </button>
                <button
                  data-testid="merge-audit-select-none-btn"
                  className="underline text-slate-600"
                  onClick={() => { setSelectedLosers({}); setDryRun(null); }}
                >
                  Clear
                </button>
              </div>
            )}
          </div>
          {allClone.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No clone-log-backed candidates found.</p>
          ) : (
            <div className="border rounded-md max-h-72 overflow-y-auto divide-y bg-white">
              {allClone.map((r) => (
                <div key={r.from_rm_id} className="flex items-center gap-3 p-2 text-xs hover:bg-slate-50">
                  <Checkbox
                    data-testid={`merge-audit-row-${r.from_rm_id}`}
                    checked={!!selectedLosers[r.from_rm_id]}
                    onCheckedChange={() => toggleLoser(r.from_rm_id)}
                  />
                  <code className="text-blue-700 font-mono w-44 truncate">{r.from_rm_id}</code>
                  <span className="text-slate-400">→</span>
                  <code className="text-slate-700 font-mono w-44 truncate">{r.into_rm_id}</code>
                  <Badge variant="outline" className="text-[10px]">
                    {r.clone_log_attribution?.new_pantone_code || "—"}
                  </Badge>
                  <span className="text-slate-500 ml-auto">{r.performed_at?.slice(0, 10)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Manual review */}
        {allManual.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer font-semibold text-amber-700">
              Manual review — {allManual.length} loser(s) without clone-log attribution
            </summary>
            <div className="border rounded-md max-h-48 overflow-y-auto divide-y bg-white mt-2">
              {allManual.map((r) => (
                <div key={r.from_rm_id} className="p-2 flex items-center gap-3">
                  <code className="text-amber-700 font-mono w-44 truncate">{r.from_rm_id}</code>
                  <span className="text-slate-400">→</span>
                  <code className="text-slate-700 font-mono w-44 truncate">{r.into_rm_id}</code>
                  <span className="text-slate-500 italic">no clone log entry</span>
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Action controls */}
        <div className="flex items-center gap-3 pt-2 border-t">
          <Button
            data-testid="merge-audit-dryrun-btn"
            onClick={runDryRun} disabled={loading || allClone.length === 0}
            variant="outline" size="sm"
          >
            <Eye className="h-4 w-4 mr-2" /> Dry-run
          </Button>
          <div className="flex items-center gap-2 text-xs">
            <Switch
              data-testid="merge-audit-force-toggle"
              checked={force}
              onCheckedChange={(v) => { setForce(v); setDryRun(null); }}
            />
            <Label className="text-xs cursor-pointer" onClick={() => setForce(!force)}>
              Force (bypass stock-movement guard)
            </Label>
          </div>
          <div className="flex-1" />
          <Button
            data-testid="merge-audit-apply-btn"
            onClick={applyUnmerge}
            disabled={!dryRun || applying || (guardBlockedCount > 0 && !force)}
            variant="default" size="sm"
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Play className={`h-4 w-4 mr-2 ${applying ? "animate-spin" : ""}`} />
            Apply Unmerge
          </Button>
        </div>

        {/* Dry-run report */}
        {dryRun && (
          <div className="border rounded-md bg-white p-3 text-xs space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold">Dry-run report</h4>
              <div className="flex gap-2">
                <Badge variant="outline">backed: {dryRun.clone_log_backed_total}</Badge>
                <Badge variant="outline">manual: {dryRun.manual_review_total}</Badge>
                {guardBlockedCount > 0 && (
                  <Badge variant="outline" className="border-amber-400 text-amber-700">
                    guard-blocked: {guardBlockedCount}
                  </Badge>
                )}
              </div>
            </div>
            {dryRun.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 space-y-1">
                {dryRun.warnings.map((w, i) => (
                  <p key={i} className="text-amber-800 flex items-start gap-1">
                    <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" /> {w}
                  </p>
                ))}
              </div>
            )}
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="p-1">Loser</th>
                    <th className="p-1">Keep</th>
                    <th className="p-1">Bidso SKU</th>
                    <th className="p-1">Pantone</th>
                    <th className="p-1">BOM repoint</th>
                    <th className="p-1">Will apply</th>
                  </tr>
                </thead>
                <tbody>
                  {dryRun.per_loser_report?.map((r) => (
                    <tr key={r.from_rm_id} className="border-b last:border-0">
                      <td className="p-1 font-mono">{r.from_rm_id}</td>
                      <td className="p-1 font-mono text-slate-600">{r.into_rm_id}</td>
                      <td className="p-1 font-mono">{r.bidso_sku || "—"}</td>
                      <td className="p-1">{r.pantone_to_restore}</td>
                      <td className="p-1">
                        {r.bom_items_to_repoint?.length || 0} item
                      </td>
                      <td className="p-1">
                        {r.loser_state_ok && !r.guard_blocks_apply ? (
                          <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                        ) : (
                          <AlertTriangle className="h-3 w-3 text-amber-600" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const SummaryStat = ({ label, value, tone }) => {
  const toneCls = {
    neutral: "bg-slate-100 text-slate-700",
    good: "bg-emerald-100 text-emerald-700",
    warn: "bg-amber-100 text-amber-700",
  }[tone] || "bg-slate-100 text-slate-700";
  return (
    <div className={`rounded-md px-3 py-2 ${toneCls}`}>
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
      <div className="text-xl font-semibold">{value ?? "—"}</div>
    </div>
  );
};
